import { AwsRum, AwsRumConfig } from 'aws-rum-web';
import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
} from 'react';
import { useUserAlias } from 'src/common/hooks/useUserAlias';
import { Page } from 'src/common/types/Page';
import { RumEvents } from 'src/common/types/RumEvents';

interface AppRumContextProps {
  recordError: (error: any) => void;
  recordEvent: (
    eventType: RumEvents,
    eventData: Record<string, unknown>,
  ) => void;
  recordPageView: (pageId: Page) => void;
  addSessionAttributes: (
    sessionAttributes: Record<string, boolean | string | number>,
  ) => void;
}

const AppRumContext = createContext<AppRumContextProps>({
  recordError: () => {},
  recordEvent: () => {},
  recordPageView: () => {},
  addSessionAttributes: () => {},
});

type AwsRumProviderProps = AwsRumConfig & {
  children: React.ReactNode;
};

export const AwsRumProvider = ({ children }: AwsRumProviderProps) => {
  const { alias } = useUserAlias();
  const [awsRum] = useState<AwsRum | null>(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);

  /**
   * Wait for authentication to complete before initializing RUM
   */
  useEffect(() => {
    const checkAuth = async () => {
      try {
        const { Auth } = await import('aws-amplify');
        await Auth.currentAuthenticatedUser();
        setIsAuthenticated(true);
        console.debug('RUM: User authenticated, ready to initialize');
      } catch (error) {
        console.debug('RUM: Waiting for authentication', error);
      }
    };
    checkAuth();
  }, []);

  /**
   * Initialize RUM client after authentication
   * TEMPORARILY DISABLED: RUM initialization causes "Unauthenticated access is not supported" errors
   * because it tries to access the identity pool before OAuth redirect completes.
   * TODO: Re-enable once proper auth state management is implemented
   */
  useEffect(() => {
    console.debug(
      'RUM: Initialization disabled to prevent unauthenticated access errors',
    );
  }, [isAuthenticated, alias, awsRum]);

  /**
   * Add session attributes to the AWS RUM client
   * @param sessionAttributes - The session attributes to add
   */
  const handleAddSessionAttributes = useCallback(
    (sessionAttributes: Record<string, boolean | string | number>) => {
      if (!awsRum) {
        console.debug('RUM: Client not initialized yet');
        return;
      }
      awsRum.addSessionAttributes(sessionAttributes);
    },
    [awsRum],
  );

  /**
   * Record event using the AWS RUM client
   * @param eventType - The event type to record
   * @param eventData - The event data to record
   */
  const handleRecordEvent = useCallback(
    (eventType: RumEvents, eventData: Record<string, unknown>) => {
      if (!awsRum) {
        console.debug('RUM: Client not initialized yet');
        return;
      }
      awsRum.recordEvent(eventType, eventData);
      awsRum.dispatch();
    },
    [awsRum],
  );

  /**
   * Record page view using the AWS RUM client
   * @param pageId - The page id to record
   */
  const handleRecordPageView = useCallback(
    (pageId: Page, pageTags?: string[]) => {
      if (!awsRum) {
        console.debug('RUM: Client not initialized yet');
        return;
      }
      awsRum.recordPageView({ pageId, pageTags });
      awsRum.dispatch();
    },
    [awsRum],
  );

  /**
   * Record error using the AWS RUM client
   * @param error - The error to record
   */
  const handleRecordError = useCallback(
    (error: any) => {
      if (!awsRum) {
        console.debug('RUM: Client not initialized yet');
        return;
      }
      awsRum.recordError(error);
      awsRum.dispatch();
    },
    [awsRum],
  );

  return (
    <AppRumContext.Provider
      value={{
        recordError: handleRecordError,
        recordEvent: handleRecordEvent,
        recordPageView: handleRecordPageView,
        addSessionAttributes: handleAddSessionAttributes,
      }}
    >
      {children}
    </AppRumContext.Provider>
  );
};

export const useAwsRum = () => useContext(AppRumContext);
